import React, { useState, useEffect } from 'react';
import { apiUrls, getHttpHeaders } from '../config/api';
import { CSNode } from './tree/treeHelpers';

interface BookContentManagerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedStructure: CSNode | null;
}

interface Book {
  id: number;
  title: string;
  description?: string;
  grade_id?: number;
  subject_id?: number;
  grade_name?: string;
  subject_name?: string;
  author?: string;
  price?: string;
  image?: string;
}

interface BookLink {
  id: number;
  book_id: number;
  content_structure_id: number;
  book?: Book;
  is_active: boolean;
  created_at: string;
}

const BookContentManager: React.FC<BookContentManagerProps> = ({
  isOpen,
  onClose,
  selectedStructure
}) => {
  const [availableBooks, setAvailableBooks] = useState<Book[]>([]);
  const [linkedBooks, setLinkedBooks] = useState<BookLink[]>([]);
  const [loadingBooks, setLoadingBooks] = useState(false);
  const [loadingLinks, setLoadingLinks] = useState(false);
  
  // Pagination state
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  
  // Filter states
  const [filterGrade, setFilterGrade] = useState<string>('');
  const [filterSubject, setFilterSubject] = useState<string>('');
  const [grades, setGrades] = useState<Array<{id: number, name: string}>>([]);
  const [subjects, setSubjects] = useState<Array<{id: number, name: string}>>([]);

  // Fetch linked books for the selected structure
  const fetchLinkedBooks = async (structureId: number) => {
    try {
      setLoadingLinks(true);
      console.log(`📚 Fetching linked books for structure ${structureId}...`);
      
      const response = await fetch(`${apiUrls.contentLibrary.contentStructures}/${structureId}/book-links`, {
        headers: getHttpHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        const books = data.books || data.links || [];
        setLinkedBooks(books);
        console.log(`✅ Loaded ${books.length} linked books for structure ${structureId}`);
      } else {
        console.error('Failed to fetch linked books:', response.status);
        setLinkedBooks([]);
      }
    } catch (error) {
      console.error('❌ Error fetching linked books:', error);
      setLinkedBooks([]);
    } finally {
      setLoadingLinks(false);
    }
  };

  // Fetch available books - get ALL books, we'll filter client-side
  const fetchAvailableBooks = async () => {
    try {
      setLoadingBooks(true);
      // Fetch ALL books without any server-side filtering
      const apiUrl = `${apiUrls.books.list}`;
      console.log(`📖 Fetching all available books...`);
      console.log(`📖 API URL: ${apiUrl}`);
      
      const response = await fetch(apiUrl, {
        headers: {
          ...getHttpHeaders(),
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      console.log(`📖 Response status: ${response.status}`);

      if (response.ok) {
        const data = await response.json();
        const books = data.books || data || [];
        console.log(`✅ API Response:`, data);
        console.log(`✅ Loaded ${books.length} total books from database`);
        
        // Show ALL books - let admin manually filter using dropdowns
        setAvailableBooks(books);
        console.log(`✅ ${books.length} books loaded and ready to display`);
      } else if (response.status === 304) {
        console.warn('⚠️ 304 Not Modified - trying to parse cached response');
        // 304 means cached, try to use it anyway
        try {
          const data = await response.json();
          const books = data.books || data || [];
          setAvailableBooks(books);
          console.log(`✅ Used cached data: ${books.length} books`);
        } catch {
          console.error('❌ Could not parse 304 response');
          setAvailableBooks([]);
        }
      } else {
        console.error('❌ Failed to fetch available books:', response.status);
        setAvailableBooks([]);
      }
    } catch (error) {
      console.error('❌ Error fetching available books:', error);
      setAvailableBooks([]);
    } finally {
      setLoadingBooks(false);
    }
  };

  // Link a book to the structure
  const handleAddBookToStructure = async (bookId: number) => {
    if (!selectedStructure) return;

    try {
      console.log(`📚 Linking book ${bookId} to structure ${selectedStructure.id}...`);
      
      const response = await fetch(`${apiUrls.contentLibrary.contentStructures}/${selectedStructure.id}/book-links`, {
        method: 'POST',
        headers: { ...getHttpHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          book_id: bookId
        })
      });

      if (response.ok) {
        console.log('✅ Book linked successfully');
        // Refresh linked books
        await fetchLinkedBooks(selectedStructure.id);
        // Refresh available books to update the list
        await fetchAvailableBooks();
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Failed to link book:', errorData);
        alert(`Failed to link book: ${(errorData as any).message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error linking book:', error);
      alert('Error linking book. Please try again.');
    }
  };

  // Remove a book link from the structure
  const handleRemoveBookFromStructure = async (linkId: number) => {
    if (!selectedStructure) return;

    try {
      console.log(`🗑️ Removing book link ${linkId} from structure ${selectedStructure.id}...`);
      
      const response = await fetch(`${apiUrls.contentLibrary.contentStructures}/${selectedStructure.id}/book-links/${linkId}`, {
        method: 'DELETE',
        headers: getHttpHeaders()
      });

      if (response.ok) {
        console.log('✅ Book unlinked successfully');
        // Refresh linked books
        await fetchLinkedBooks(selectedStructure.id);
        // Refresh available books to update the list
        await fetchAvailableBooks();
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Failed to unlink book:', errorData);
        alert(`Failed to remove book: ${(errorData as any).message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error removing book:', error);
      alert('Error removing book. Please try again.');
    }
  };

  // Fetch grades and subjects for filters
  const fetchGradesAndSubjects = async () => {
    try {
      const [gradesRes, subjectsRes] = await Promise.all([
        fetch(apiUrls.books.grades, { headers: getHttpHeaders() }),
        fetch(apiUrls.books.subjects, { headers: getHttpHeaders() })
      ]);

      if (gradesRes.ok) {
        const gradesData = await gradesRes.json();
        setGrades(gradesData.grades || gradesData || []);
      }

      if (subjectsRes.ok) {
        const subjectsData = await subjectsRes.json();
        setSubjects(subjectsData.subjects || subjectsData || []);
      }
    } catch (error) {
      console.error('Error fetching grades/subjects:', error);
    }
  };

  // Load data when modal opens or structure changes
  useEffect(() => {
    if (isOpen && selectedStructure) {
      console.log(`📚 BookContentManager opened for structure:`, {
        id: selectedStructure.id,
        name: selectedStructure.name,
        grade_id: selectedStructure.grade_id,
        subject_id: selectedStructure.subject_id,
        full_structure: selectedStructure
      });
      
      // Reset filters to show all books
      setFilterGrade('');
      setFilterSubject('');
      setCurrentPage(1);
      
      fetchLinkedBooks(selectedStructure.id);
      fetchGradesAndSubjects();
      
      // Always fetch all books - let admin manually filter
      // Same structure can be used for multiple grades
      console.log(`📖 Loading all books - no automatic filtering`);
      fetchAvailableBooks();
    }
  }, [isOpen, selectedStructure]);

  // Apply client-side filters (only when filters are explicitly selected)
  const filteredAvailableBooks = availableBooks.filter(book => {
    // Only filter if user has selected something from dropdown (filterGrade/filterSubject are not empty)
    const matchesGrade = !filterGrade || 
                         book.grade_id?.toString() === filterGrade || 
                         book.grade_name?.includes(filterGrade) ||
                         book.grade_name === grades.find(g => g.id.toString() === filterGrade)?.name;
    
    const matchesSubject = !filterSubject || 
                           book.subject_id?.toString() === filterSubject || 
                           book.subject_name?.includes(filterSubject) ||
                           book.subject_name === subjects.find(s => s.id.toString() === filterSubject)?.name;
    
    return matchesGrade && matchesSubject;
  });
  
  console.log(`📊 Filtering: ${availableBooks.length} total books → ${filteredAvailableBooks.length} after filters (Grade: ${filterGrade || 'All'}, Subject: ${filterSubject || 'All'})`);

  if (!isOpen || !selectedStructure) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '900px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111827' }}>
              📚 Book Content Management
            </h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
              Structure: <strong>{selectedStructure.name}</strong>
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '4px',
              borderRadius: '4px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            ×
          </button>
        </div>

        {/* Currently Linked Books */}
        <div style={{ marginBottom: '32px' }}>
          <h4 style={{ 
            margin: '0 0 16px 0', 
            fontSize: '16px', 
            fontWeight: '600', 
            color: '#374151',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            📖 Currently Linked Books 
            <span style={{
              background: '#dbeafe',
              color: '#1e40af',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '500'
            }}>
              {linkedBooks.length}
            </span>
          </h4>
          
          {loadingLinks ? (
            <div style={{ 
              padding: '20px', 
              textAlign: 'center', 
              color: '#6b7280',
              fontSize: '14px'
            }}>
              Loading linked books...
            </div>
          ) : linkedBooks.length > 0 ? (
            <div style={{ display: 'grid', gap: '12px' }}>
              {linkedBooks.map((link) => (
                <div key={link.id} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: '#f9fafb',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontWeight: '500', 
                      fontSize: '15px', 
                      color: '#111827',
                      marginBottom: '4px'
                    }}>
                      {link.book?.title || `Book ${link.book_id}`}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                      Grade: {link.book?.grade_name || 'Unknown'} | Subject: {link.book?.subject_name || 'Unknown'}
                    </div>
                    {link.book?.description && (
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#6b7280', 
                        lineHeight: '1.4',
                        maxWidth: '500px'
                      }}>
                        {link.book.description.length > 120 
                          ? `${link.book.description.substring(0, 120)}...` 
                          : link.book.description
                        }
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemoveBookFromStructure(link.id)}
                    style={{
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease',
                      marginLeft: '16px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ 
              padding: '24px', 
              textAlign: 'center', 
              color: '#6b7280',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px dashed #d1d5db'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>📚</div>
              <div style={{ fontSize: '14px' }}>No books currently linked to this content structure.</div>
            </div>
          )}
        </div>

        {/* Add Books Section */}
        <div>
          <h4 style={{ 
            margin: '0 0 16px 0', 
            fontSize: '16px', 
            fontWeight: '600', 
            color: '#374151',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            ➕ Add Books
            <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '400' }}>
              (Use filters below to find specific books)
            </span>
          </h4>

          {/* Filter and Pagination Controls */}
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '12px', 
            marginBottom: '16px'
          }}>
            {/* Grade Filter */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                Grade:
              </label>
              <select
                value={filterGrade}
                onChange={(e) => {
                  setFilterGrade(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  height: '42px'
                }}
              >
                <option value="">All Grades</option>
                {grades.map(grade => (
                  <option key={grade.id} value={grade.id}>{grade.name}</option>
                ))}
              </select>
            </div>

            {/* Subject Filter */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                Subject:
              </label>
              <select
                value={filterSubject}
                onChange={(e) => {
                  setFilterSubject(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  height: '42px'
                }}
              >
                <option value="">All Subjects</option>
                {subjects.map(subject => (
                  <option key={subject.id} value={subject.id}>{subject.name}</option>
                ))}
              </select>
            </div>

            {/* Items per page selector */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                Show:
              </label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  height: '42px'
                }}
              >
                <option value="10">10 items</option>
                <option value="20">20 items</option>
                <option value="50">50 items</option>
                <option value="100">All</option>
              </select>
            </div>
          </div>
          
          {loadingBooks ? (
            <div style={{ 
              padding: '20px', 
              textAlign: 'center', 
              color: '#6b7280',
              fontSize: '14px'
            }}>
              Loading available books...
            </div>
          ) : availableBooks.length > 0 ? (
            <>
              <div style={{ 
                display: 'grid', 
                gap: '12px', 
                maxHeight: '400px', 
                overflow: 'auto',
                padding: '4px'
              }}>
                {filteredAvailableBooks.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((book) => {
                const isAlreadyLinked = linkedBooks.some(link => link.book_id === book.id);
                
                return (
                  <div key={book.id} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: isAlreadyLinked ? '#f3f4f6' : 'white',
                    opacity: isAlreadyLinked ? 0.7 : 1,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!isAlreadyLinked) {
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontWeight: '500', 
                        fontSize: '15px', 
                        color: isAlreadyLinked ? '#6b7280' : '#111827',
                        marginBottom: '4px'
                      }}>
                        {book.title}
                      </div>
                      <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                        Grade: {book.grade_name || 'Unknown'} | Subject: {book.subject_name || 'Unknown'}
                        {book.author && ` | Author: ${book.author}`}
                      </div>
                      {book.description && (
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#6b7280', 
                          lineHeight: '1.4',
                          maxWidth: '500px'
                        }}>
                          {book.description.length > 120 
                            ? `${book.description.substring(0, 120)}...` 
                            : book.description
                          }
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleAddBookToStructure(book.id)}
                      disabled={isAlreadyLinked}
                      style={{
                        background: isAlreadyLinked ? '#9ca3af' : '#059669',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: isAlreadyLinked ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.2s ease',
                        marginLeft: '16px'
                      }}
                      onMouseEnter={(e) => {
                        if (!isAlreadyLinked) {
                          e.currentTarget.style.backgroundColor = '#047857';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isAlreadyLinked) {
                          e.currentTarget.style.backgroundColor = '#059669';
                        }
                      }}
                    >
                      {isAlreadyLinked ? 'Already Linked' : 'Add Book'}
                    </button>
                  </div>
                );
              })}
              </div>
              
              {/* Pagination controls */}
              {filteredAvailableBooks.length > itemsPerPage && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}>
                  <div style={{ color: '#6b7280' }}>
                    Showing <strong>{((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredAvailableBooks.length)}</strong> of <strong>{filteredAvailableBooks.length}</strong> books
                    {(filterGrade || filterSubject) && ' (filtered)'}
                  </div>
                  
                  {Math.ceil(filteredAvailableBooks.length / itemsPerPage) > 1 && (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: currentPage === 1 ? '#e5e7eb' : '#3b82f6',
                          color: currentPage === 1 ? '#9ca3af' : 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                        }}
                      >
                        ← Prev
                      </button>
                      
                      <span style={{ color: '#3b82f6', fontWeight: '600', fontSize: '12px' }}>
                        Page {currentPage} of {Math.ceil(filteredAvailableBooks.length / itemsPerPage)}
                      </span>
                      
                      <button
                        onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredAvailableBooks.length / itemsPerPage), p + 1))}
                        disabled={currentPage === Math.ceil(filteredAvailableBooks.length / itemsPerPage)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: currentPage === Math.ceil(filteredAvailableBooks.length / itemsPerPage) ? '#e5e7eb' : '#3b82f6',
                          color: currentPage === Math.ceil(filteredAvailableBooks.length / itemsPerPage) ? '#9ca3af' : 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: currentPage === Math.ceil(filteredAvailableBooks.length / itemsPerPage) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div style={{ 
              padding: '24px', 
              textAlign: 'center', 
              color: '#6b7280',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px dashed #d1d5db'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>📖</div>
              <div style={{ fontSize: '14px' }}>
                {filteredAvailableBooks.length === 0 && availableBooks.length > 0
                  ? 'No books match the selected filters. Try changing the Grade or Subject filter above.'
                  : 'No books available in the system. Please add books first in Book Management section.'
                }
              </div>
              {filteredAvailableBooks.length === 0 && availableBooks.length > 0 && (
                <button
                  onClick={() => {
                    setFilterGrade('');
                    setFilterSubject('');
                    setCurrentPage(1);
                  }}
                  style={{
                    marginTop: '12px',
                    padding: '8px 16px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          marginTop: '24px',
          paddingTop: '16px',
          borderTop: '1px solid #e5e7eb'
        }}>
          <button
            onClick={onClose}
            style={{
              background: '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 32px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#047857'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#059669'}
          >
            ✓ Save & Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookContentManager;
